import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Copy, Upload } from "lucide-react";
import { ImportarPlanilla } from "@/components/ImportarPlanilla";
import { GenerarHuecos } from "@/components/GenerarHuecos";
import { useOcupacion, SemanaNav, OcupacionProf } from "@/components/Ocupacion";
import { api, errMsg, HUECOS_TIPO, textOn } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageTitle } from "@/components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DIAS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const ROW = 22;
const toMin = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const toHm = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const INI = toMin("10:00");
const FIN = toMin("20:15");
const SLOTS = Array.from({ length: (FIN - INI) / 15 }, (_, i) => toHm(INI + i * 15));

const HuecoDialog = ({ h, profs, onClose, onDone }) => {
  const [f, setF] = useState(null);
  useEffect(() => {
    setF(h);
  }, [h]);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      if (f.id) await api.put(`/huecos-tipo/${f.id}`, f);
      else await api.post("/huecos-tipo", f);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async () => {
    try {
      await api.delete(`/huecos-tipo/${f.id}`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!h} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="hueco-dialog">
        <DialogHeader><DialogTitle className="font-[Outfit]">{f.id ? "Editar hueco" : "Nuevo hueco"} · {DIAS[f.dia]}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(HUECOS_TIPO).map(([k, t]) => (
            <button key={k} type="button" onClick={() => setF({ ...f, tipo: k })} data-testid={`hueco-tipo-${k}`}
              className="rounded-md border px-2 py-1.5 text-xs font-medium transition-colors" style={f.tipo === k ? { background: t.color, borderColor: t.color, color: "#fff" } : { borderColor: `${t.color}66`, color: t.color }}>{t.label}</button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <label className="text-[11px] font-semibold uppercase text-slate-500 col-span-3">Profesional
            <select className="field-select mt-1" value={f.profesional_id} onChange={set("profesional_id")} data-testid="hueco-prof">{profs.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select>
          </label>
          <label className="text-[11px] font-semibold uppercase text-slate-500">Inicio<Input type="time" step="900" value={f.inicio} onChange={set("inicio")} data-testid="hueco-inicio" className="mt-1" /></label>
          <label className="text-[11px] font-semibold uppercase text-slate-500">Fin<Input type="time" step="900" value={f.fin} onChange={set("fin")} data-testid="hueco-fin" className="mt-1" /></label>
          <label className="text-[11px] font-semibold uppercase text-slate-500">Nota<Input value={f.nota} onChange={set("nota")} data-testid="hueco-nota" className="mt-1" placeholder="opcional" /></label>
        </div>
        <div className="flex gap-2">
          {f.id && <Button variant="outline" className="text-rose-700" onClick={del} data-testid="hueco-delete"><Trash2 className="h-4 w-4" /></Button>}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} data-testid="hueco-save" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const HuecoBox = ({ h, limite, onEdit, onResized }) => {
  const [fin, setFin] = useState(h.fin);
  useEffect(() => {
    setFin(h.fin);
  }, [h.fin]);
  const t = HUECOS_TIPO[h.tipo];
  const startResize = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const y0 = e.clientY;
    const f0 = toMin(h.fin);
    let nuevo = h.fin;
    const mv = (ev) => {
      const m = Math.min(limite, Math.max(toMin(h.inicio) + 15, f0 + Math.round((ev.clientY - y0) / ROW) * 15));
      nuevo = toHm(m);
      setFin(nuevo);
    };
    const up = () => {
      window.removeEventListener("mousemove", mv);
      window.removeEventListener("mouseup", up);
      if (nuevo !== h.fin) onResized(h, nuevo);
    };
    window.addEventListener("mousemove", mv);
    window.addEventListener("mouseup", up);
  };
  return (
    <div className="absolute left-1 right-1 rounded overflow-hidden" data-testid={`planilla-hueco-${h.id}`}
      style={{ top: ((toMin(h.inicio) - INI) / 15) * ROW + 1, height: ((toMin(fin) - toMin(h.inicio)) / 15) * ROW - 2, background: `${t.color}22`, color: t.color, border: `1px dashed ${t.color}` }}>
      <button type="button" onClick={() => onEdit(h)} className="h-full w-full px-1.5 py-0.5 text-left text-[10px] font-semibold leading-tight">
        {h.inicio}–{fin} · {t.label}{h.nota ? ` · ${h.nota}` : ""}
      </button>
      <div onMouseDown={startResize} title="Arrastra para alargar o acortar" data-testid={`planilla-resize-${h.id}`}
        className="absolute inset-x-0 bottom-0 h-1.5 cursor-ns-resize hover:bg-black/20" />
    </div>
  );
};

const Columna = ({ p, huecos, dia, onNew, onEdit, onResized }) => {
  const mios = huecos.filter((h) => h.profesional_id === p.id && h.dia === dia);
  const limite = (h) => Math.min(FIN, ...mios.filter((x) => x.inicio >= h.fin && x.id !== h.id).map((x) => toMin(x.inicio)));
  return (
  <div className="relative border-l border-slate-200" style={{ height: SLOTS.length * ROW }} data-testid={`planilla-col-${p.id}`}>
    {SLOTS.map((s, i) => (
      <button key={s} type="button" onClick={() => onNew(p, s)} title={`Añadir hueco ${s}`} data-testid={`planilla-cell-${p.id}-${s}`}
        className={`absolute inset-x-0 hover:bg-sky-50 ${s.endsWith(":00") ? "border-t border-slate-200" : "border-t border-slate-100"}`} style={{ top: i * ROW, height: ROW }} />
    ))}
    {mios.map((h) => <HuecoBox key={h.id} h={h} limite={limite(h)} onEdit={onEdit} onResized={onResized} />)}
  </div>
  );
};

const copiarDia = async (huecos, dia, profIds, destinos) => {
  const borrar = huecos.filter((h) => destinos.includes(h.dia) && profIds.includes(h.profesional_id));
  const orig = huecos.filter((h) => h.dia === dia && profIds.includes(h.profesional_id));
  await Promise.all(destinos.flatMap((d) => orig.map(({ id, ...h }) => api.post("/huecos-tipo", { ...h, dia: d }))));
  await Promise.all(borrar.map((h) => api.delete(`/huecos-tipo/${h.id}`)));
};

export default function Planillas() {
  const [centro, setCentro] = useState("MADRID");
  const [dia, setDia] = useState(0);
  const [profs, setProfs] = useState([]);
  const [huecos, setHuecos] = useState([]);
  const [sel, setSel] = useState(null);
  const [imp, setImp] = useState(false);
  const [gen, setGen] = useState(false);
  const ocup = useOcupacion(centro, huecos);
  const { user } = useAuth();
  const load = useCallback(() => {
    api.get("/huecos-tipo", { params: { centro } }).then((r) => setHuecos(r.data));
  }, [centro]);
  useEffect(() => {
    api.get("/profesionales", { params: { centro } }).then((r) => setProfs(r.data));
    load();
  }, [centro, load]);
  const onNew = (p, s) => setSel({ profesional_id: p.id, dia, centro, inicio: s, fin: toHm(Math.min(toMin(s) + 45, FIN)), tipo: "terapia", nota: "" });
  const resized = async (h, fin) => {
    try {
      await api.put(`/huecos-tipo/${h.id}`, { ...h, fin });
      toast.success(`Hueco ${h.inicio}–${fin}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    load();
  };
  const vaciar = async (p) => {
    const n = huecos.filter((h) => h.profesional_id === p.id && h.dia === dia).length;
    if (!n) return toast.info(`${p.nombre} no tiene huecos el ${DIAS[dia].toLowerCase()}`);
    if (!window.confirm(`¿Borrar los ${n} huecos de ${p.nombre} del ${DIAS[dia].toLowerCase()} (${centro === "MADRID" ? "Madrid" : "Alcobendas"})? Las citas ya dadas no se tocan.`)) return;
    const copia = huecos.filter((h) => h.profesional_id === p.id && h.dia === dia);
    try {
      const { data } = await api.delete("/huecos-tipo", { params: { profesional_id: p.id, dia, centro } });
      toast.success(`${data.borrados} huecos borrados`, {
        duration: 10000,
        action: {
          label: "Deshacer",
          onClick: async () => {
            try {
              const r = await api.post("/huecos-tipo/restaurar", { huecos: copia });
              toast.success(`${r.data.restaurados} huecos recuperados`);
            } catch (e) {
              toast.error(errMsg(e));
            }
            load();
          },
        },
      });
    } catch (e) {
      toast.error(errMsg(e));
    }
    load();
  };
  const copiar = async () => {
    const otros = [0, 1, 2, 3, 4].filter((i) => i !== dia);
    if (!window.confirm(`¿Copiar los huecos del ${DIAS[dia].toLowerCase()} al resto de días laborables (${otros.map((i) => DIAS[i]).join(", ")})? Se sustituyen los huecos que ya tengan esos días.`)) return;
    try {
      await copiarDia(huecos, dia, profs.map((p) => p.id), otros);
      toast.success("Huecos copiados al resto de la semana");
    } catch (e) {
      toast.error(`No se ha podido copiar todo: ${errMsg(e)}`);
    }
    load();
  };

  return (
    <div data-testid="planillas-page">
      <PageTitle title="Semana tipo" subtitle="Marca los huecos de cada profesional. Se pintan solos en la agenda todas las semanas (menos en festivos) y desaparecen cuando se da una cita encima." />
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {["MADRID", "ALCOBENDAS"].map((c) => (
          <button key={c} data-testid={`planilla-tab-${c.toLowerCase()}`} onClick={() => setCentro(c)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${centro === c ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {c === "MADRID" ? "Madrid" : "Alcobendas"}
          </button>
        ))}
        <span className="mx-2 h-5 w-px bg-slate-200" />
        {DIAS.map((d, i) => (
          <button key={d} data-testid={`planilla-dia-${i}`} onClick={() => setDia(i)}
            className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${dia === i ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{d}</button>
        ))}
        <Button size="sm" variant="outline" className="ml-auto h-8 text-xs" onClick={() => setGen(true)} data-testid="planilla-generar">Crear huecos vacíos</Button>
        {user?.role === "admin" && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setImp(true)} data-testid="planilla-importar"><Upload className="h-3.5 w-3.5 mr-1" />Importar Excel</Button>}
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={copiar} data-testid="planilla-copiar"><Copy className="h-3.5 w-3.5 mr-1" />Copiar este día al resto</Button>
      </div>
      <SemanaNav o={ocup} />
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="planilla-grid">
        <div className="grid min-w-max" style={{ gridTemplateColumns: `56px repeat(${profs.length}, minmax(150px, 1fr))` }}>
          <div className="sticky left-0 bg-white" />
          {profs.map((p) => <div key={p.id} className="px-2 py-2 text-center text-xs font-semibold" style={{ background: p.color, color: textOn(p.color) }}>{p.nombre}<OcupacionProf x={ocup.occ[p.id]} id={p.id} />
            <button type="button" onClick={() => vaciar(p)} data-testid={`planilla-vaciar-${p.id}`} className="mt-1 rounded bg-white/70 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 transition-colors hover:bg-white">Vaciar este día</button></div>)}
          <div className="sticky left-0 bg-white">
            {SLOTS.map((s) => <div key={s} style={{ height: ROW }} className={`pr-1 text-right text-[10px] font-[JetBrains_Mono] ${s.endsWith(":00") ? "text-slate-700 font-semibold" : "text-slate-400"}`}>{s.endsWith(":00") ? s : s.slice(3)}</div>)}
          </div>
          {profs.map((p) => <Columna key={p.id} p={p} huecos={huecos} dia={dia} onNew={onNew} onEdit={setSel} onResized={resized} />)}
        </div>
      </div>
      <HuecoDialog h={sel} profs={profs} onClose={() => setSel(null)} onDone={() => { setSel(null); load(); }} />
      <GenerarHuecos open={gen} centro={centro} profs={profs} onClose={() => setGen(false)} onDone={() => { setGen(false); load(); }} />
      <ImportarPlanilla open={imp} centro={centro} profs={profs} onClose={() => setImp(false)} onDone={() => { setImp(false); load(); }} />
    </div>
  );
}
